<script setup lang="ts">
/**
 * AuraHeader - Header copied from CallPage pattern
 * DatePicker inline + Filters in side panel
 */
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { MenuIcon } from '@heroicons/vue/outline';
import { useAuraResponsive } from '@/composables/useAuraResponsive';

import { useAuraStore } from '@/stores/modules/aura';
import { SelectOptionsService } from '@/services';
import {
    endOfUtcDay,
    formatShortDate,
    normalizeUtcDateRange,
    startOfUtcDay,
} from '@/utils/datetime';
import AuraFilters from './AuraFilters.vue';
import type {
    AuraFilters as AuraFiltersType,
    SelectOptionsServiceGetUsersResult,
    SelectOptionsServiceGetQueuesResult,
    SelectOptionsServiceGetMediasResult,
} from '@/types';

const props = defineProps<{
    verticalName?: string;
    showMenu?: boolean;
    compact?: boolean;
}>();

const emit = defineEmits<{
    menu: [];
}>();

const { t } = useI18n();
const store = useAuraStore();
const { isMobile } = useAuraResponsive();

const ROLLING_WINDOW_DAYS = 92;

const maximumSelectableDate = computed(() => endOfUtcDay(new Date()));

// Piso do range: nunca antes da ativação do AURA para o level, e nunca
// além da janela rolante de 92 dias usada pelo backend (clampDateWindow).
const minimumSelectableDate = computed(() => {
    const rollingFloor = startOfUtcDay(maximumSelectableDate.value);
    rollingFloor.setDate(rollingFloor.getDate() - ROLLING_WINDOW_DAYS);

    const activatedAt = store.verticalActivatedAt ? new Date(store.verticalActivatedAt) : null;
    if (activatedAt && !Number.isNaN(activatedAt.getTime()) && activatedAt > rollingFloor) {
        return startOfUtcDay(activatedAt);
    }
    return rollingFloor;
});

const clampDate = (value: Date) => {
    const normalizedDate = startOfUtcDay(value);
    if (normalizedDate < minimumSelectableDate.value) return new Date(minimumSelectableDate.value);
    if (normalizedDate > maximumSelectableDate.value) return new Date(maximumSelectableDate.value);
    return normalizedDate;
};

const normalizeDateRange = (dateFrom: Date, dateTo: Date): [Date, Date] => {
    return normalizeUtcDateRange(clampDate(dateFrom), clampDate(dateTo));
};

const getDefaultDateRange = (): [Date, Date] => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return normalizeDateRange(sevenDaysAgo, today);
};

// O contexto (com a data de ativação) pode chegar depois do range default
// já ter sido aplicado no mount; re-clampa se o range atual ficou fora do
// novo piso.
watch(minimumSelectableDate, () => {
    const activeDateFrom = store.activeFilters?.dateFrom;
    const activeDateTo = store.activeFilters?.dateTo;
    if (!activeDateFrom || !activeDateTo) return;

    const [dateFrom, dateTo] = normalizeDateRange(new Date(activeDateFrom), new Date(activeDateTo));
    const newDateFrom = dateFrom.toISOString();
    const newDateTo = dateTo.toISOString();
    if (newDateFrom === activeDateFrom && newDateTo === activeDateTo) return;

    store.setFilters({
        ...store.activeFilters,
        dateFrom: newDateFrom,
        dateTo: newDateTo,
    });
});

// Load users, queues and medias for name lookup
const users = ref<SelectOptionsServiceGetUsersResult[]>([]);
const queues = ref<SelectOptionsServiceGetQueuesResult[]>([]);
const medias = ref<SelectOptionsServiceGetMediasResult[]>([]);

onMounted(async () => {
    // Set default date range: last week (7 days ago to today) if not set
    const defaultDateRange = getDefaultDateRange();
    const [dateFrom, dateTo] = normalizeDateRange(
        store.activeFilters?.dateFrom
            ? new Date(store.activeFilters.dateFrom)
            : defaultDateRange[0],
        store.activeFilters?.dateTo ? new Date(store.activeFilters.dateTo) : defaultDateRange[1],
    );
    store.setFilters({
        ...store.activeFilters,
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
    });

    try {
        const [usersRes, queuesRes, mediasRes] = await Promise.all([
            SelectOptionsService.getUsers(),
            SelectOptionsService.getQueues(),
            SelectOptionsService.getMedias(),
        ]);
        users.value = usersRes.data;
        queues.value = queuesRes.data;
        medias.value = mediasRes.data;
    } catch (error) {
        console.error('Failed to load filter options:', error);
    }
});

// Date range - inline like CallPage (line 300)
const dateRange = computed<Date[]>({
    get() {
        const dateFrom = store.activeFilters?.dateFrom;
        const dateTo = store.activeFilters?.dateTo;
        if (dateFrom && dateTo) {
            return [new Date(dateFrom), new Date(dateTo)];
        }
        return [];
    },
    set(newRange) {
        if (newRange && newRange.length === 2) {
            const [normalizedFrom, normalizedTo] = normalizeDateRange(
                new Date(newRange[0]),
                new Date(newRange[1]),
            );
            const dateFrom = normalizedFrom.toISOString();
            const dateTo = normalizedTo.toISOString();
            if (
                dateFrom !== store.activeFilters?.dateFrom ||
                dateTo !== store.activeFilters?.dateTo
            ) {
                store.setFilters({
                    ...store.activeFilters,
                    dateFrom,
                    dateTo,
                });
            }
        } else {
            // Reset to default last week (7 days) if date range is cleared
            const [dateFrom, dateTo] = getDefaultDateRange();
            store.setFilters({
                ...store.activeFilters,
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
            });
        }
    },
});

// Handle remove filter
function handleRemoveFilter(field: keyof AuraFiltersType | 'period') {
    const newFilters = { ...store.activeFilters };
    if (field === 'dateFrom' || field === 'dateTo' || field === 'period') {
        // Reset to default last week (7 days) when date filter is removed
        const [dateFrom, dateTo] = getDefaultDateRange();
        newFilters.dateFrom = dateFrom.toISOString();
        newFilters.dateTo = dateTo.toISOString();
    } else {
        (newFilters as Record<string, unknown>)[field] = undefined;
    }
    store.setFilters(newFilters);
}

// Filter display names mapping
const filterDisplayNames: Record<string, string> = {
    dateFrom: t('aura.filters.dateRange'),
    operatorId: t('aura.filters.operator'),
    queueId: t('aura.filters.queue'),
    mediaId: t('aura.filters.media'),
};

// Build active filters array for display
const activeFiltersList = computed(() => {
    const filters: Array<{ field: string; name: string; label: string }> = [];
    const f = store.activeFilters;
    if (!f) return filters;

    if (f.dateFrom && f.dateTo) {
        filters.push({
            field: 'dateFrom',
            name: filterDisplayNames.dateFrom,
            label: `${formatShortDate(f.dateFrom)} - ${formatShortDate(f.dateTo)}`,
        });
    }
    if (f.operatorId) {
        // Handle case where operatorId might be an object or primitive
        const operatorIdValue =
            typeof f.operatorId === 'object' && f.operatorId !== null
                ? (f.operatorId as { id?: number }).id || Number(f.operatorId)
                : f.operatorId;
        const user = users.value.find((u) => u.id === operatorIdValue);
        filters.push({
            field: 'operatorId',
            name: filterDisplayNames.operatorId,
            label: user?.name || String(operatorIdValue),
        });
    }
    if (f.queueId) {
        // Handle case where queueId might be an object or primitive
        const queueIdValue =
            typeof f.queueId === 'object' && f.queueId !== null
                ? (f.queueId as { id?: number }).id || Number(f.queueId)
                : f.queueId;
        const queue = queues.value.find((q) => q.id === queueIdValue);
        filters.push({
            field: 'queueId',
            name: filterDisplayNames.queueId,
            label: queue?.name || String(queueIdValue),
        });
    }
    if (f.mediaId) {
        // Handle case where mediaId might be an object or primitive
        const mediaIdValue =
            typeof f.mediaId === 'object' && f.mediaId !== null
                ? (f.mediaId as { value?: string }).value || String(f.mediaId)
                : f.mediaId;
        const media = medias.value.find((m) => m.value === mediaIdValue);
        filters.push({
            field: 'mediaId',
            name: filterDisplayNames.mediaId,
            label: media?.label || mediaIdValue,
        });
    }
    return filters;
});

const visibleActiveFilters = computed(() =>
    isMobile.value
        ? activeFiltersList.value.filter((filter) => filter.field !== 'dateFrom')
        : activeFiltersList.value,
);
</script>

<template>
    <header
        class="aura-header flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E5E5] bg-white px-4 py-3 shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] sm:px-6 sm:py-4"
    >
        <!-- Left: Branding -->
        <div class="aura-header-brand flex items-center gap-2">
            <button
                type="button"
                v-if="props.showMenu && props.compact"
                class="-ml-1 rounded-lg p-2 text-[#242B59] transition-colors hover:bg-[#F5F6F8] focus:outline-none focus:ring-2 focus:ring-[#48DDC3]"
                aria-label="Abrir histórico de conversas"
                @click="emit('menu')"
            >
                <MenuIcon class="h-6 w-6" />
            </button>
            <div
                class="aura-header-title flex items-center gap-2 rounded-full bg-[#E6F9F5] px-3 py-1.5"
            >
                <div class="h-2 w-2 rounded-full bg-[#48DDC3]"></div>
                <span class="text-sm font-bold uppercase tracking-wider text-[#242B59]">
                    {{ props.verticalName || t('aura.chat.title') }}
                </span>
            </div>
        </div>

        <!-- Right: Period (inline like CallPage) + Filters -->
        <div class="aura-header-controls flex items-center gap-2 sm:gap-4">
            <div class="aura-header-date-picker hidden w-64 sm:block">
                <UiDatePicker
                    v-model="dateRange"
                    range
                    multiCalendars
                    :min-date="minimumSelectableDate"
                    :max-date="maximumSelectableDate"
                />
            </div>

            <!-- Filters in side panel -->
            <AuraFilters />
        </div>

        <div class="aura-header-mobile-date w-full sm:hidden">
            <UiDatePicker
                v-model="dateRange"
                range
                :multi-calendars="false"
                :min-date="minimumSelectableDate"
                :max-date="maximumSelectableDate"
            />
        </div>
    </header>

    <!-- Active Filters Display - copied from CallPage line 312-328 -->
    <div
        v-if="visibleActiveFilters.length"
        class="aura-active-filters flex flex-wrap gap-2 rounded border-t border-gray-200 bg-gray-50 p-2 ring-1 ring-gray-300"
    >
        <UiBadge
            v-for="filter in visibleActiveFilters"
            :key="filter.field"
            removable
            @remove="handleRemoveFilter(filter.field as keyof AuraFiltersType)"
        >
            {{ filter.name }}: {{ filter.label }}
        </UiBadge>
    </div>
</template>

<style scoped>
@media (max-width: 767px) {
    .aura-header {
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
    }

    .aura-header-title {
        padding: 0.25rem 0.625rem;
    }

    .aura-header-title span {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
    }

    .aura-header-mobile-date :deep(.dp__input) {
        min-height: 2.5rem;
        padding: 0.5rem 0.625rem 0.5rem 2.5rem;
        font-size: 0.75rem;
    }
}

@media (orientation: landscape) and (max-height: 500px) {
    .aura-header {
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
    }

    .aura-header-brand,
    .aura-header-controls {
        gap: 0.5rem;
    }

    .aura-header-title {
        padding: 0.25rem 0.625rem;
    }

    .aura-header-title span {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
    }

    .aura-header-date-picker {
        width: 13rem;
    }

    .aura-header-date-picker :deep(.dp__input) {
        min-height: 2.25rem;
        padding: 0.375rem 0.5rem 0.375rem 2.25rem;
        font-size: 0.75rem;
    }

    .aura-active-filters {
        gap: 0.25rem;
        padding: 0.25rem 0.5rem;
    }
}
</style>
